import { useEffect, useMemo, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { BaseButton } from '@repo/common';

import { ReactComponent as ArrowBottomIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-bottom.svg';
import { ReactComponent as ArrowTopIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-top.svg';
import { ReactComponent as CheckIcon } from '@repo/common/assets/icons/16px/icon_16px_check.svg';

const normalizeOptions = (options) =>
  options.map((option, index) =>
    typeof option === 'string'
      ? { value: option, label: option }
      : { value: option?.value ?? String(index), label: option?.label ?? '' },
  );

/** Compact 50px dropdown used by the mobile Pathfinder screens. */
function PathfinderMobileDropdown({
  options = [],
  value,
  defaultValue = '',
  onChange,
  placeholder = 'Please select',
  className = '',
  menuClassName = '',
}) {
  const rootRef = useRef(null);
  const [internalValue, setInternalValue] = useState(defaultValue);
  const [isOpen, setIsOpen] = useState(false);
  const normalizedOptions = useMemo(() => normalizeOptions(options), [options]);
  const selectedValue = value === undefined ? internalValue : value;
  const selectedOption = normalizedOptions.find(
    (option) => option.value === selectedValue,
  );
  const ArrowIcon = isOpen ? ArrowTopIcon : ArrowBottomIcon;

  useEffect(() => {
    const closeOnOutsidePointer = (event) => {
      if (!rootRef.current?.contains(event.target)) setIsOpen(false);
    };

    document.addEventListener('mousedown', closeOnOutsidePointer);
    return () =>
      document.removeEventListener('mousedown', closeOnOutsidePointer);
  }, []);

  const selectOption = (option) => {
    if (value === undefined) setInternalValue(option.value);
    onChange?.(option.value, option);
    setIsOpen(false);
  };

  return (
    <div ref={rootRef} className={twMerge('relative w-full', className)}>
      <BaseButton
        type='text'
        size='m'
        htmlType='button'
        typo={false}
        isStatic
        leftalign
        gap='gap-2'
        iconPosition='after'
        icon={
          <ArrowIcon
            className={twMerge(
              'size-4 shrink-0',
              isOpen ? 'text-text-success' : 'text-text-placeholder',
            )}
            aria-hidden
          />
        }
        aria-haspopup='listbox'
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
        textWrapperClass={twMerge(
          'h-auto flex-1 truncate text-left typo-b14Strong',
          selectedOption || isOpen
            ? 'text-text-success'
            : 'text-text-placeholder',
        )}
        className={twMerge(
          'h-[50px] w-full min-w-0 cursor-pointer items-center gap-2 rounded-[12px] border-[1.6px] px-4 py-2 text-left transition-colors',
          isOpen
            ? 'border-surface-green40 bg-surface-lightgray60'
            : 'border-transparent bg-surface-lightgray60',
        )}
      >
        {selectedOption?.label ?? placeholder}
      </BaseButton>

      {isOpen ? (
        <div
          role='listbox'
          className={twMerge(
            'absolute left-0 top-[calc(100%+4px)] z-30 w-full rounded-[12px] border border-grayscale-gray02 bg-grayscale-gray00 p-[6px] shadow-popup-02',
            menuClassName,
          )}
        >
          {normalizedOptions.map((option) => {
            const isSelected = option.value === selectedValue;
            return (
              <BaseButton
                key={option.value}
                type='text'
                size='m'
                htmlType='button'
                typo={false}
                isStatic
                leftalign
                gap='gap-[2px]'
                icon={
                  <span className='flex h-[18px] w-4 items-center justify-center'>
                    {isSelected ? (
                      <CheckIcon className='size-4 text-text-primary' />
                    ) : null}
                  </span>
                }
                role='option'
                aria-selected={isSelected}
                onClick={() => selectOption(option)}
                textWrapperClass='h-auto typo-b14Strong text-text-primary'
                className='h-[38px] w-full min-w-0 cursor-pointer items-center rounded-[8px] border-0 bg-transparent px-2 py-[10px] text-left hover:bg-grayscale-gray01'
              >
                {option.label}
              </BaseButton>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export default PathfinderMobileDropdown;
